import { useMemo, useState } from 'react';
import { api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import Card from '../components/Card';
import StatCard from '../components/StatCard';
import InlineForm from '../components/InlineForm';
import ConfirmDialog from '../components/ConfirmDialog';
import Icon from '../components/Icon';
import { Loading, ErrorState, EmptyState } from '../components/States';
import { number } from '../lib/format';
import { ICONS } from '../lib/icons';
import { sameId } from '../lib/ids';

// Price management: what a user group is charged per unit of a SKU — the sell
// side only. The buy side is OCI's directory price, which comes back with the
// usage itself and is never entered here, so margin is always measured on the
// Cost & Billing report against what OCI actually charged.
const BLANK = {
  skuId: '',
  skuName: '',
  unit: '',
  currency: 'USD',
  // One band spanning everything is a flat price, and it is what the form opens
  // on: most SKUs are billed at a single rate, and the ladder should cost nothing
  // to ignore. Bands are held as strings because they are typed into inputs; the
  // conversion to numbers happens once, on submit.
  bands: [{ rangeMin: '0', rangeMax: '', billingPrice: '', discount: '' }],
};

// Copy price seeds the group's table from the provider's published list — every
// SKU the catalogue rates in one currency, in one write. What lands is ordinary
// overrides: editable, deletable, and unaffected by a later catalogue sync.
//
// Existing rates are kept by default. A price entered by hand is a decision, and
// a bulk copy that silently replaced it would lose work with no way back.
const BLANK_COPY = {
  providerId: '',
  currency: 'USD',
  existing: 'keep',
  // Which groups the copy writes. Defaults to the one on screen: a bulk write
  // across every client's prices is a bigger action than the form it shares, and
  // it should be chosen rather than inherited.
  scope: 'group',
};

// Copy to group reuses this group's own manually-set prices on one or more
// other groups — a shortcut for pricing a new client the same as an existing
// one. Only manual prices travel: a price copied from the published list is
// already one click away from any group via Copy price, so sending it again
// here would just be a slower way to do the same thing.
const BLANK_COPY_TO_GROUP = {
  targetGroupIds: [],
  existing: 'keep',
};

// The two currencies the platform bills in. The published catalogue carries
// ~50, but a price is only ever set in a currency usage is actually reported in
// — OCI reports in the tenancy's own — so offering all 50 invites a rate that
// can never match a row. The backend stays permissive: it has to store whatever
// a tenancy reports, and this list is the console's choice, not a constraint on
// the data.
const CURRENCIES = ['USD', 'SGD'];

// A row already stored in some other currency still has to be editable without
// being silently re-denominated, so its own currency joins the list.
const currencyOptions = (selected) =>
  !selected || CURRENCIES.includes(selected)
    ? CURRENCIES
    : [...CURRENCIES, selected];

// Unit prices are small numbers (fractions of a cent per hour), so they are
// shown at full precision rather than the 2-decimal currency format.
const rate = (v, cur) =>
  `${(v ?? 0).toLocaleString('en-US', { maximumFractionDigits: 6 })} ${cur || ''}`.trim();

// A band's lower bound, as a volume. Thresholds run to the millions (950,000
// requests, 10 TB), so they are grouped rather than printed raw.
const volume = (v) => (v ?? 0).toLocaleString('en-US', { maximumFractionDigits: 4 });

// How a band is labelled in the list: the volume at which it starts charging.
// The first band always starts at zero, so it says "first" rather than "0+",
// which reads as a threshold that is not one.
const bandLabel = (band, i) =>
  i === 0 ? 'first' : `${volume(band.rangeMin)}+`;

export default function PriceManagement() {
  const { isAdmin } = useAuth();
  const groupsReq = useAsync(() => api.getUserGroups(), []);
  const groups = useMemo(() => groupsReq.data || [], [groupsReq.data]);

  const [groupId, setGroupId] = useState('');
  const activeGroupId = groupId || groups[0]?.id || '';
  const activeGroup = groups.find((g) => sameId(g.id, activeGroupId));

  const pricesReq = useAsync(
    () => (activeGroupId ? api.getGroupPrices(activeGroupId) : Promise.resolve([])),
    [activeGroupId]
  );
  const prices = useMemo(() => pricesReq.data || [], [pricesReq.data]);

  const [query, setQuery] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('');
  // Price management's default view is what was priced by hand — a copy of the
  // published catalogue can seed several hundred rows in one click, and those
  // would otherwise bury the handful of rates an operator actually set. The
  // synced ones are never hidden, only collapsed behind this toggle.
  const [showAll, setShowAll] = useState(false);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [copy, setCopy] = useState(null);
  const [copying, setCopying] = useState(false);
  const [copyError, setCopyError] = useState(null);
  const [copyResult, setCopyResult] = useState(null);

  const [copyToGroup, setCopyToGroup] = useState(null);
  const [copyingToGroup, setCopyingToGroup] = useState(false);
  const [copyToGroupError, setCopyToGroupError] = useState(null);
  const [copyToGroupResult, setCopyToGroupResult] = useState(null);

  const providersReq = useAsync(() => api.getProviders(), []);
  const providers = useMemo(() => providersReq.data || [], [providersReq.data]);

  // The catalogue is read only while the copy form is open, and only to say how
  // much of it the copy would cover: how many SKUs carry a published rate in the
  // currency selected. Nothing here is what gets written — the copy itself reads
  // the catalogue on the backend.
  const catalogueReq = useAsync(
    () =>
      copy?.providerId
        ? api.getProviderSkus(copy.providerId, copy.currency)
        : Promise.resolve(null),
    [copy?.providerId, copy?.currency]
  );
  // Both billing currencies are always offered rather than narrowed to what the
  // catalogue publishes: the count below says how many SKUs the selected one
  // rates, so "0 of 658" is visible before the copy runs, and a currency the
  // price list does not carry at all is named in the error rather than quietly
  // missing from the picker.
  //
  // A SKU with no band in this currency is not copied at all — it keeps falling
  // back to OCI's cost, which is a different thing from being priced at zero.
  const catalogueRated = useMemo(() => {
    const skus = catalogueReq.data?.data;
    if (!skus) return null;
    return {
      rated: skus.filter((s) => (s.prices || []).length > 0).length,
      total: skus.length,
    };
  }, [catalogueReq.data]);

  const [confirm, setConfirm] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState(null);

  // The currencies this group actually holds prices in, billing order first. A
  // SKU can be priced once per currency, so the same part number appears twice
  // in a two-currency group — the filter is what makes that list readable.
  const priceCurrencies = useMemo(() => {
    const held = new Set(prices.map((p) => p.currency).filter(Boolean));
    return [
      ...CURRENCIES.filter((c) => held.has(c)),
      ...[...held].filter((c) => !CURRENCIES.includes(c)).sort(),
    ];
  }, [prices]);

  // A filter the group has no rows for would hide everything with nothing to
  // explain it, so it falls back to showing all.
  const activeCurrency = priceCurrencies.includes(currencyFilter)
    ? currencyFilter
    : '';

  // Hidden by the manual-only default, not by the search or currency filter —
  // counted on the full set so the toggle's hint stays right regardless of what
  // else is filtered.
  const syncedCount = useMemo(() => prices.filter((p) => !p.manual).length, [prices]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return prices.filter((p) => {
      if (!showAll && !p.manual) return false;
      if (activeCurrency && p.currency !== activeCurrency) return false;
      if (!q) return true;
      return `${p.skuId} ${p.skuName} ${p.unit}`.toLowerCase().includes(q);
    });
  }, [prices, query, activeCurrency, showAll]);

  const stats = useMemo(() => {
    const currencies = new Set(prices.map((p) => p.currency).filter(Boolean));
    const tiered = prices.filter((p) => (p.bands || []).length > 1).length;
    const manual = prices.filter((p) => p.manual).length;
    return { count: prices.length, currencies: currencies.size, tiered, manual };
  }, [prices]);

  // Copy to group only ever offers other groups: copying a group's prices into
  // itself is not a choice this UI needs to present.
  const otherGroups = useMemo(
    () => groups.filter((g) => !sameId(g.id, activeGroupId)),
    [groups, activeGroupId]
  );

  const closeCopy = () => {
    setCopy(null);
    setCopyError(null);
  };

  const closeCopyToGroup = () => {
    setCopyToGroup(null);
    setCopyToGroupError(null);
  };

  const startCreate = () => {
    closeCopy();
    closeCopyToGroup();
    setEditing(null);
    setFormError(null);
    setForm({ ...BLANK });
  };

  const startEdit = (p) => {
    closeCopy();
    closeCopyToGroup();
    setEditing(p.id);
    setFormError(null);
    const bands = (p.bands || []).map((b) => ({
      rangeMin: String(b.rangeMin ?? 0),
      rangeMax: b.rangeMax ? String(b.rangeMax) : '',
      billingPrice: String(b.billingPrice ?? ''),
      discount: b.discount ? String(b.discount) : '',
    }));
    setForm({
      skuId: p.skuId,
      skuName: p.skuName || '',
      unit: p.unit || '',
      currency: p.currency || 'USD',
      // A stored price always has at least one band, but an empty ladder would
      // leave the form with nothing to type into rather than failing loudly.
      bands: bands.length ? bands : [{ ...BLANK.bands[0] }],
    });
  };

  const closeForm = () => {
    setForm(null);
    setEditing(null);
    setFormError(null);
  };

  // Only one form at a time: both write to the same table, and a half-typed
  // single price beside a bulk copy of the whole catalogue reads as one action.
  const startCopy = () => {
    closeForm();
    closeCopyToGroup();
    setCopyError(null);
    setCopyResult(null);
    setCopy({ ...BLANK_COPY, providerId: providers[0]?.id || '' });
  };

  const startCopyToGroup = () => {
    closeForm();
    closeCopy();
    setCopyToGroupError(null);
    setCopyToGroupResult(null);
    setCopyToGroup({ ...BLANK_COPY_TO_GROUP });
  };

  // Checkbox values come straight from the groups array rather than through a
  // DOM input's string value, so the ids compare the same way they are stored.
  const toggleCopyTarget = (groupId) =>
    setCopyToGroup((c) => ({
      ...c,
      targetGroupIds: c.targetGroupIds.some((id) => sameId(id, groupId))
        ? c.targetGroupIds.filter((id) => !sameId(id, groupId))
        : [...c.targetGroupIds, groupId],
    }));

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const updateBand = (i, key) => (e) =>
    setForm((f) => ({
      ...f,
      bands: f.bands.map((b, at) => (at === i ? { ...b, [key]: e.target.value } : b)),
    }));

  // A new band opens where the one above it ends, which is the only place it can
  // start without leaving a gap in the ladder.
  const addBand = () =>
    setForm((f) => {
      const last = f.bands[f.bands.length - 1];
      return {
        ...f,
        bands: [
          ...f.bands,
          { rangeMin: last?.rangeMax || '', rangeMax: '', billingPrice: '', discount: '' },
        ],
      };
    });

  // The first band is never removable: it is the one that prices the opening
  // units of every month, and a ladder without it prices them at nothing.
  const removeBand = (i) =>
    setForm((f) => ({ ...f, bands: f.bands.filter((_, at) => at !== i || at === 0) }));

  const updateCopy = (key) => (e) =>
    setCopy((c) => ({ ...c, [key]: e.target.value }));

  const runCopy = async (e) => {
    e.preventDefault();
    if (!copy.providerId) {
      setCopyError('Select a provider');
      return;
    }
    setCopying(true);
    setCopyError(null);
    try {
      const payload = {
        providerId: copy.providerId,
        currency: copy.currency,
        overwrite: copy.existing === 'replace',
      };
      const res =
        copy.scope === 'all'
          ? await api.copyAllGroupPrices(payload)
          : await api.copyGroupPrices(activeGroupId, payload);
      setCopyResult(res);
      closeCopy();
      pricesReq.reload();
    } catch (err) {
      setCopyError(err.message);
    } finally {
      setCopying(false);
    }
  };

  const updateCopyToGroupExisting = (e) =>
    setCopyToGroup((c) => ({ ...c, existing: e.target.value }));

  const runCopyToGroup = async (e) => {
    e.preventDefault();
    if (copyToGroup.targetGroupIds.length === 0) {
      setCopyToGroupError('Select at least one user group to copy into');
      return;
    }
    setCopyingToGroup(true);
    setCopyToGroupError(null);
    try {
      const payload = {
        targetGroupIds: copyToGroup.targetGroupIds,
        overwrite: copyToGroup.existing === 'replace',
      };
      const res = await api.copyGroupPricesToGroups(activeGroupId, payload);
      setCopyToGroupResult(res);
      closeCopyToGroup();
    } catch (err) {
      setCopyToGroupError(err.message);
    } finally {
      setCopyingToGroup(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.skuId.trim()) {
      setFormError('SKU is required');
      return;
    }
    // The first band is pinned to zero here as well as on the backend, so what
    // the form shows and what gets stored cannot drift apart.
    const bands = form.bands.map((b, i) => ({
      rangeMin: i === 0 ? 0 : parseFloat(b.rangeMin) || 0,
      rangeMax: parseFloat(b.rangeMax) || 0,
      billingPrice: parseFloat(b.billingPrice) || 0,
      discount: parseFloat(b.discount) || 0,
    }));
    if (bands.some((b) => b.discount < 0 || b.discount > 100)) {
      setFormError('A band discount must be between 0 and 100');
      return;
    }
    for (let i = 1; i < bands.length; i += 1) {
      if (bands[i].rangeMin <= bands[i - 1].rangeMin) {
        setFormError('Each band must start above the one before it');
        return;
      }
    }
    const payload = {
      skuId: form.skuId.trim(),
      skuName: form.skuName.trim(),
      unit: form.unit.trim(),
      currency: (form.currency || 'USD').trim().toUpperCase(),
      bands,
    };
    setSaving(true);
    setFormError(null);
    try {
      if (editing) await api.updatePrice(editing, payload);
      else await api.createGroupPrice(activeGroupId, payload);
      closeForm();
      pricesReq.reload();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const removePrice = async () => {
    setConfirmBusy(true);
    setConfirmError(null);
    try {
      await api.deletePrice(confirm.id);
      setConfirm(null);
      pricesReq.reload();
    } catch (err) {
      setConfirmError(err.message);
    } finally {
      setConfirmBusy(false);
    }
  };

  if (groupsReq.loading) return <Loading label="Loading price management…" />;
  if (groupsReq.error) {
    return <ErrorState error={groupsReq.error} onRetry={groupsReq.reload} />;
  }

  return (
    <div className="stack">
      <PageHeader
        title="Price management"
        subtitle="Override the unit price of a SKU for a user group. Cost and billing are set independently."
        actions={
          <>
            <button
              className="btn btn--ghost"
              onClick={startCopy}
              disabled={!isAdmin || !activeGroupId || providers.length === 0}
              title={
                !isAdmin
                  ? 'Superadmin role required'
                  : !activeGroupId
                  ? 'Create a user group first'
                  : 'Copy the provider’s published rates into this group'
              }
            >
              <Icon name={ICONS.copy} /> Copy price
            </button>
            <button
              className="btn btn--ghost"
              onClick={startCopyToGroup}
              disabled={!isAdmin || !activeGroupId || stats.manual === 0 || otherGroups.length === 0}
              title={
                !isAdmin
                  ? 'Superadmin role required'
                  : !activeGroupId
                  ? 'Create a user group first'
                  : stats.manual === 0
                  ? 'This group has no manually set prices to copy'
                  : otherGroups.length === 0
                  ? 'Create another user group first'
                  : 'Copy this group’s manually set prices into another group'
              }
            >
              <Icon name={ICONS.copy} /> Copy to group
            </button>
            <button
              className="btn btn--primary"
              onClick={startCreate}
              disabled={!isAdmin || !activeGroupId}
              title={
                !isAdmin
                  ? 'Superadmin role required'
                  : !activeGroupId
                  ? 'Create a user group first'
                  : undefined
              }
            >
              + Add price
            </button>
          </>
        }
      />

      {groups.length === 0 ? (
        <Card>
          <EmptyState
            title="No user groups"
            detail="Prices are set per user group. Create a group under IAM first."
          />
        </Card>
      ) : (
        <>
          <div className="grid grid--kpi">
            <StatCard
              label="Priced SKUs"
              value={number(stats.count)}
              hint={
                stats.tiered
                  ? `${number(stats.tiered)} tiered · ${activeGroup?.name || '—'}`
                  : activeGroup?.name || '—'
              }
            />
            <StatCard
              label="Currencies"
              value={number(stats.currencies)}
              hint="distinct"
            />
            <StatCard
              label="Buy side"
              value="OCI"
              hint="directory price, not set here"
            />
            <StatCard
              label="OCI tenant"
              value={activeGroup?.tenancyName || '—'}
              hint="usage source"
            />
          </div>

          {copyResult ? (
            <div className="field__hint">
              <p>
                Copied <strong>{number(copyResult.copied)}</strong> published{' '}
                {copyResult.currency} rates
                {copyResult.updated
                  ? `, replaced ${number(copyResult.updated)}`
                  : ''}
                {copyResult.skipped
                  ? `, kept ${number(copyResult.skipped)} already priced`
                  : ''}{' '}
                of <strong>{number(copyResult.catalogue)}</strong> rated SKUs
                {/* A batch carries per-group results; a single copy has none. The
                    counters above are summed across groups, so the scope has to be
                    stated or "kept 2,632" reads as one group being very stale. */}
                {copyResult.results
                  ? ` across ${number(copyResult.results.length)} group${
                      copyResult.results.length === 1 ? '' : 's'
                    }.`
                  : '.'}
                {copyResult.tiered
                  ? ` ${number(copyResult.tiered)} publish more than one volume band, and the whole ladder was copied — those bill per month, at the band each unit falls in.`
                  : ''}
              </p>
              {copyResult.results?.length > 1 ? (
                <ul className="copy-breakdown">
                  {copyResult.results.map((r) => (
                    <li key={r.groupId}>
                      <strong>{r.groupName}</strong> — copied {number(r.copied)}
                      {r.updated ? `, replaced ${number(r.updated)}` : ''}
                      {r.skipped ? `, kept ${number(r.skipped)}` : ''}
                    </li>
                  ))}
                </ul>
              ) : null}
              {/* A half-written batch must not read as a success: these groups are
                  still on their old prices, and nothing else on the page says so. */}
              {copyResult.failures?.length ? (
                <p className="form-error">
                  {number(copyResult.failures.length)} group
                  {copyResult.failures.length === 1 ? ' was' : 's were'} not priced:{' '}
                  {copyResult.failures
                    .map((f) => `${f.groupName} (${f.error})`)
                    .join('; ')}
                </p>
              ) : null}
            </div>
          ) : null}

          {copyToGroupResult ? (
            <div className="field__hint">
              <p>
                Copied <strong>{number(copyToGroupResult.source)}</strong> manually
                set price{copyToGroupResult.source === 1 ? '' : 's'} from{' '}
                <strong>{copyToGroupResult.sourceGroupName}</strong> into{' '}
                {number(copyToGroupResult.results?.length || 0)} group
                {copyToGroupResult.results?.length === 1 ? '' : 's'}:{' '}
                <strong>{number(copyToGroupResult.copied)}</strong> copied
                {copyToGroupResult.updated
                  ? `, ${number(copyToGroupResult.updated)} replaced`
                  : ''}
                {copyToGroupResult.skipped
                  ? `, ${number(copyToGroupResult.skipped)} kept already priced`
                  : ''}
                .
              </p>
              {copyToGroupResult.results?.length > 1 ? (
                <ul className="copy-breakdown">
                  {copyToGroupResult.results.map((r) => (
                    <li key={r.groupId}>
                      <strong>{r.groupName}</strong> — copied {number(r.copied)}
                      {r.updated ? `, replaced ${number(r.updated)}` : ''}
                      {r.skipped ? `, kept ${number(r.skipped)}` : ''}
                    </li>
                  ))}
                </ul>
              ) : null}
              {copyToGroupResult.failures?.length ? (
                <p className="form-error">
                  {number(copyToGroupResult.failures.length)} group
                  {copyToGroupResult.failures.length === 1 ? ' was' : 's were'} not
                  priced:{' '}
                  {copyToGroupResult.failures
                    .map((f) => `${f.groupName || f.groupId} (${f.error})`)
                    .join('; ')}
                </p>
              ) : null}
            </div>
          ) : null}

          <Card
            className="card--quiet-title"
            title={`Prices — ${activeGroup?.name || 'select a group'}`}
            action={
              <input
                className="input search"
                placeholder="Search SKU, name, unit…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            }
          >
            <div className="filter-row" style={{ marginBottom: 16 }}>
              <label className="field">
                <span>User group</span>
                <select
                  className="input"
                  value={activeGroupId}
                  onChange={(e) => {
                    setGroupId(e.target.value);
                    closeForm();
                    closeCopy();
                    closeCopyToGroup();
                    // Each summary counts rows written while the group that was
                    // selected when its copy ran was the source, so it says
                    // nothing here.
                    setCopyResult(null);
                    setCopyToGroupResult(null);
                  }}
                >
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                      {g.tenancyName ? ` · ${g.tenancyName}` : ''}
                    </option>
                  ))}
                </select>
              </label>

              {priceCurrencies.length > 1 ? (
                <label className="field">
                  <span>Currency</span>
                  <select
                    className="input"
                    value={activeCurrency}
                    onChange={(e) => setCurrencyFilter(e.target.value)}
                  >
                    <option value="">All currencies</option>
                    {priceCurrencies.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              <label className="field">
                <span>Source</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 38 }}>
                  <input
                    type="checkbox"
                    checked={showAll}
                    onChange={(e) => setShowAll(e.target.checked)}
                  />
                  Show synced prices too
                  {syncedCount ? ` (${number(syncedCount)})` : ''}
                </span>
              </label>
            </div>

            {copy ? (
              <InlineForm
                title={
                  copy.scope === 'all'
                    ? 'Copy public price — all user groups'
                    : `Copy public price — ${activeGroup?.name || ''}`
                }
                onSubmit={runCopy}
                onCancel={closeCopy}
                submitting={copying}
                submitLabel={copying ? 'Copying…' : 'Copy price'}
                error={copyError}
              >
                <div className="form-grid">
                  <label className="field">
                    <span>Apply to</span>
                    <select
                      className="input"
                      value={copy.scope}
                      onChange={updateCopy('scope')}
                    >
                      <option value="group">
                        {activeGroup?.name || 'This group'} only
                      </option>
                      <option value="all">
                        All user groups ({number(groups.length)})
                      </option>
                    </select>
                    <span className="field__hint">
                      {copy.scope === 'all'
                        ? 'Every group is seeded from the same published list, in one pass. A group that fails is named in the result; the others still get their prices.'
                        : 'Only the group selected above. Every group needs pricing eventually, so the catalogue is usually copied to all of them.'}
                    </span>
                  </label>
                  <label className="field">
                    <span>Provider</span>
                    <select
                      className="input"
                      value={copy.providerId}
                      onChange={updateCopy('providerId')}
                    >
                      {providers.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                    <span className="field__hint">
                      Rates come from the provider’s published price list. Sync it
                      first if it looks stale.
                    </span>
                  </label>
                  <label className="field">
                    <span>Currency</span>
                    <select
                      className="input"
                      value={copy.currency}
                      onChange={updateCopy('currency')}
                    >
                      {CURRENCIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                    <span className="field__hint">
                      {catalogueReq.loading
                        ? 'Reading the price list…'
                        : catalogueRated
                        ? `${number(catalogueRated.rated)} of ${number(
                            catalogueRated.total
                          )} SKUs have a published ${copy.currency} rate.`
                        : 'Copies only apply to usage reported in this currency.'}
                    </span>
                  </label>
                  <label className="field">
                    <span>Prices already set</span>
                    <select
                      className="input"
                      value={copy.existing}
                      onChange={updateCopy('existing')}
                    >
                      <option value="keep">Keep — copy only unpriced SKUs</option>
                      <option value="replace">
                        Replace with the published rate
                      </option>
                    </select>
                    <span className="field__hint">
                      {copy.scope === 'all'
                        ? 'Replace overwrites rates set by hand for every group and cannot be undone.'
                        : 'Replace overwrites rates set by hand for this group and cannot be undone.'}
                    </span>
                  </label>
                </div>
                <p className="field__hint">
                  Each SKU is copied as an ordinary price you can edit or delete.
                  A SKU with several volume bands is copied band for band, so it
                  bills per calendar month at the band each unit falls in. A SKU
                  the catalogue does not rate in this currency is left unpriced —
                  it keeps billing at OCI cost. One currency per run: a rate is
                  only ever set in the currency the usage is reported in.
                </p>
              </InlineForm>
            ) : null}

            {copyToGroup ? (
              <InlineForm
                title={`Copy to group — from ${activeGroup?.name || ''}`}
                onSubmit={runCopyToGroup}
                onCancel={closeCopyToGroup}
                submitting={copyingToGroup}
                submitLabel={copyingToGroup ? 'Copying…' : 'Copy to group'}
                error={copyToGroupError}
              >
                <div className="form-grid">
                  <div className="field field--wide">
                    <span>Copy into</span>
                    <div className="table-wrap">
                      <table className="table table--dense">
                        <thead>
                          <tr>
                            <th aria-label="select" />
                            <th>User group</th>
                          </tr>
                        </thead>
                        <tbody>
                          {otherGroups.map((g) => (
                            <tr key={g.id}>
                              <td className="num">
                                <input
                                  type="checkbox"
                                  checked={copyToGroup.targetGroupIds.some((id) =>
                                    sameId(id, g.id)
                                  )}
                                  onChange={() => toggleCopyTarget(g.id)}
                                  aria-label={`Copy into ${g.name}`}
                                />
                              </td>
                              <td className="cell-primary">
                                {g.name}
                                {g.tenancyName ? ` · ${g.tenancyName}` : ''}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <span className="field__hint">
                      {number(stats.manual)} manually set price
                      {stats.manual === 1 ? '' : 's'} from {activeGroup?.name || 'this group'}{' '}
                      will be offered to every group checked above. Prices copied
                      from the published list are never included — they are
                      already one click away from any group via Copy price.
                    </span>
                  </div>
                  <label className="field">
                    <span>Prices already set</span>
                    <select
                      className="input"
                      value={copyToGroup.existing}
                      onChange={updateCopyToGroupExisting}
                    >
                      <option value="keep">Keep — copy only unpriced SKUs</option>
                      <option value="replace">Replace with {activeGroup?.name || 'this group'}’s rate</option>
                    </select>
                    <span className="field__hint">
                      Replace overwrites rates a target group already set by hand
                      and cannot be undone.
                    </span>
                  </label>
                </div>
              </InlineForm>
            ) : null}

            {form ? (
              <InlineForm
                title={editing ? 'Edit price' : `Add price — ${activeGroup?.name || ''}`}
                onSubmit={submit}
                onCancel={closeForm}
                submitting={saving}
                submitLabel={editing ? 'Save changes' : 'Add price'}
                error={formError}
              >
                <div className="form-grid">
                  <label className="field">
                    <span>SKU part number</span>
                    <input
                      className="input"
                      placeholder="e.g. B93114"
                      value={form.skuId}
                      onChange={update('skuId')}
                      autoFocus
                      required
                    />
                    <span className="field__hint">
                      Must match the SKU the provider reports, or the override
                      will never apply.
                    </span>
                  </label>
                  <label className="field">
                    <span>SKU name (optional)</span>
                    <input
                      className="input"
                      placeholder="e.g. Virtual Machine Standard - X7"
                      value={form.skuName}
                      onChange={update('skuName')}
                    />
                  </label>
                  <label className="field">
                    <span>Unit (optional)</span>
                    <input
                      className="input"
                      placeholder="e.g. OCPU Hours"
                      value={form.unit}
                      onChange={update('unit')}
                    />
                  </label>
                  <label className="field">
                    <span>Currency</span>
                    <select
                      className="input"
                      value={form.currency}
                      onChange={update('currency')}
                    >
                      {currencyOptions(form.currency).map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                    <span className="field__hint">
                      Applies only to usage reported in this currency. The same
                      SKU can be priced once per currency.
                    </span>
                  </label>
                  <div className="field field--wide">
                    <span>Billing unit price</span>
                    <div className="table-wrap">
                      <table className="table table--dense">
                        <thead>
                          <tr>
                            <th>From (quantity)</th>
                            <th>To</th>
                            <th className="num">Price per unit</th>
                            <th className="num">Discount %</th>
                            <th aria-label="actions" />
                          </tr>
                        </thead>
                        <tbody>
                          {form.bands.map((band, i) => (
                            <tr key={i}>
                              <td>
                                {/* The ladder always opens at zero, so the first
                                    band has nothing to decide. */}
                                <input
                                  className="input"
                                  type="number"
                                  min="0"
                                  step="any"
                                  value={i === 0 ? '0' : band.rangeMin}
                                  disabled={i === 0}
                                  onChange={updateBand(i, 'rangeMin')}
                                />
                              </td>
                              <td>
                                <input
                                  className="input"
                                  type="number"
                                  min="0"
                                  step="any"
                                  placeholder="no limit"
                                  value={band.rangeMax}
                                  onChange={updateBand(i, 'rangeMax')}
                                />
                              </td>
                              <td className="num">
                                <input
                                  className="input"
                                  type="number"
                                  min="0"
                                  step="any"
                                  placeholder="0.00"
                                  value={band.billingPrice}
                                  onChange={updateBand(i, 'billingPrice')}
                                />
                              </td>
                              <td className="num">
                                <input
                                  className="input"
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.01"
                                  placeholder="0"
                                  value={band.discount}
                                  onChange={updateBand(i, 'discount')}
                                />
                              </td>
                              <td className="num">
                                {i > 0 ? (
                                  <button
                                    type="button"
                                    className="btn btn--danger-ghost btn--sm"
                                    onClick={() => removeBand(i)}
                                  >
                                    Remove
                                  </button>
                                ) : null}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="row-actions">
                      <button type="button" className="btn btn--ghost btn--sm" onClick={addBand}>
                        + Add band
                      </button>
                    </div>
                    <span className="field__hint">
                      What the group is charged per unit. The buy side is OCI’s
                      directory price — margin is shown on Cost &amp; Billing.
                      {form.bands.length > 1
                        ? ' Bands are counted over a calendar month, and each one charges only the units that fall inside it — the same way the published list reads.'
                        : ' One band is a flat rate; add another to charge a different rate above a monthly volume.'}
                    </span>
                  </div>
                </div>
              </InlineForm>
            ) : null}

            {pricesReq.loading ? (
              <Loading label="Loading prices…" />
            ) : pricesReq.error ? (
              <ErrorState error={pricesReq.error} onRetry={pricesReq.reload} />
            ) : filtered.length === 0 ? (
              <EmptyState
                title={
                  prices.length === 0
                    ? 'No prices set'
                    : !showAll && syncedCount > 0
                    ? 'No manually set prices'
                    : 'No SKU matches'
                }
                detail={
                  prices.length === 0
                    ? 'Every SKU is billed at OCI cost until you set a price for it. Copy price seeds the whole catalogue at the provider’s published rate.'
                    : !showAll && syncedCount > 0
                    ? `${number(syncedCount)} price${syncedCount === 1 ? '' : 's'} copied from the published list and never edited by hand ${syncedCount === 1 ? 'is' : 'are'} hidden. Check “Show synced prices too” to see them.`
                    : activeCurrency
                    ? `Nothing priced in ${activeCurrency} matches. Try clearing the search or the currency filter.`
                    : 'Try clearing the search.'
                }
              />
            ) : (
              <div className="table-wrap">
                <table className="table table--dense">
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Name</th>
                      <th>Unit</th>
                      <th className="num">Billing price</th>
                      <th>Updated</th>
                      {showAll ? <th>Source</th> : null}
                      {isAdmin ? <th aria-label="actions" /> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((p) => (
                      <tr key={p.id}>
                        <td className="cell-primary mono">{p.skuId}</td>
                        <td>{p.skuName || '—'}</td>
                        <td className="cell-type">{p.unit || '—'}</td>
                        <td className="num cell-total">
                          {(p.bands || []).length <= 1 ? (
                            <>
                              {rate(p.bands?.[0]?.billingPrice, p.currency)}
                              {p.bands?.[0]?.discount > 0 ? ` −${p.bands[0].discount}%` : ''}
                            </>
                          ) : (
                            // Every band, not a summary: a ladder shown as its
                            // first rate reads as a flat price, which is exactly
                            // the confusion storing one number used to cause.
                            <div className="price-ladder">
                              {p.bands.map((band, i) => (
                                <div key={i}>
                                  <span className="badge badge--muted table__tag">
                                    {bandLabel(band, i)}
                                  </span>{' '}
                                  {rate(band.billingPrice, p.currency)}
                                  {band.discount > 0 ? ` −${band.discount}%` : ''}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td>{new Date(p.updatedAt).toLocaleDateString()}</td>
                        {showAll ? (
                          <td>
                            <span className={`badge ${p.manual ? 'badge--info' : 'badge--muted'} table__tag`}>
                              {p.manual ? 'manual' : 'synced'}
                            </span>
                          </td>
                        ) : null}
                        {isAdmin ? (
                          <td className="num">
                            <div className="row-actions">
                              <button
                                className="btn btn--ghost btn--sm"
                                onClick={() => startEdit(p)}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn--danger-ghost btn--sm"
                                onClick={() => setConfirm(p)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {confirm ? (
        <ConfirmDialog
          title="Delete price override"
          message={`Remove the price for ${confirm.skuId}?`}
          detail="Usage for this SKU goes back to the provider’s own rate for this group."
          confirmLabel="Delete price"
          busy={confirmBusy}
          error={confirmError}
          onConfirm={removePrice}
          onCancel={() => {
            setConfirm(null);
            setConfirmError(null);
          }}
        />
      ) : null}
    </div>
  );
}
